import { useEffect } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { fugueLabProjects } from '@/data/portfolio'
import { Separator } from '@/components/ui'
import { ImageGallery } from '@/components/ImageGallery'
import { BeforeAfter } from '@/components/BeforeAfter'
import { ProjectMetrics } from '@/components/ProjectMetrics'
import { ProjectTestimonialComponent } from '@/components/ProjectTestimonial'
import { RelatedProjects } from '@/components/RelatedProjects'
import { PortfolioCTA } from '@/components/PortfolioCTA'
import { useSEO, createPortfolioSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { trackPortfolioView } from '@/lib/analytics'

export default function PortfolioItemPage() {
  const { slug } = useParams<{ slug: string }>()

  const project = fugueLabProjects.find((p) => p.slug === slug)

  useEffect(() => {
    if (slug) trackPortfolioView(slug)
  }, [slug])

  // Hooks must be called before any early returns
  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const contentRef = useScrollReveal<HTMLDivElement>('.content-section', { stagger: 0.1 })

  // SEO configuration
  useSEO({
    title: project ? `${project.title} | Portfolio` : 'Project Not Found',
    description: project?.description || 'Portfolio project by Fugue Lab',
    url: `/portfolio/${slug}`,
    image: project?.image,
    type: 'article',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Portfolio', url: '/portfolio' },
      ...(project ? [{ name: project.title, url: `/portfolio/${project.slug}` }] : []),
    ],
    schema: project
      ? createPortfolioSchema({
          name: project.title,
          description: project.description,
          image: project.image,
          url: project.url,
          clientName: project.client,
        })
      : undefined,
  })

  if (!slug || !project) {
    return <Navigate to="/portfolio" replace />
  }

  // Get related projects (same client types)
  const relatedProjects = fugueLabProjects.filter((p) => {
    if (p.slug === project.slug) return false
    // Match by client types or service types
    const hasSameServiceType = project.serviceTypes?.some((type) => p.serviceTypes?.includes(type))
    const hasSameClientType =
      project.clientTypes && p.clientTypes && project.clientTypes.some((type) => p.clientTypes?.includes(type))
    return hasSameServiceType || hasSameClientType
  })

  return (
    <div ref={contentRef} className="mx-auto max-w-4xl space-y-12 px-4 py-12">
      <section ref={heroRef} className="space-y-2">
        <h1 className="text-3xl font-bold">{project.title}</h1>
        <p className="text-muted-foreground">{project.description}</p>
        <Separator />
      </section>

      {/* Image Gallery (or single image) */}
      <section>
        {project.images && project.images.length > 0 ? (
          <ImageGallery images={project.images} alt={project.title} />
        ) : (
          <img
            src={project.image}
            alt={project.title}
            width={1000}
            height={600}
            className="w-full rounded-lg border object-cover shadow-md"
          />
        )}
      </section>

      {/* Before/After Comparison */}
      {project.beforeAfter && project.beforeAfter.length > 0 && <BeforeAfter pairs={project.beforeAfter} />}

      {project.client && (
        <section className="content-section space-y-4">
          <h2 className="text-2xl font-semibold">Client Profile</h2>
          <p>{project.client}</p>
        </section>
      )}

      {project.summary && (
        <section className="content-section space-y-4">
          <h2 className="text-2xl font-semibold">Project Summary</h2>
          <p>{project.summary}</p>
        </section>
      )}

      {project.objectives && project.objectives.length > 0 && (
        <section className="content-section space-y-4">
          <h2 className="text-2xl font-semibold">Objectives</h2>
          <ul>
            {project.objectives.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      {project.implementation && project.implementation.length > 0 && (
        <section className="content-section space-y-4">
          <h2 className="text-2xl font-semibold">What We Implemented</h2>
          <ul className="list-inside list-disc">
            {project.implementation.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      {project.results && project.results.length > 0 && (
        <section className="content-section space-y-4">
          <h2 className="text-2xl font-semibold">Results</h2>
          <ul className="list-inside list-disc">
            {project.results.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-block text-sm underline"
            >
              Visit Live Site &rarr;
            </a>
          )}
        </section>
      )}

      {/* If no detailed case study content, show simpler view */}
      {!project.summary && !project.objectives && !project.implementation && !project.results && project.url && (
        <section className="space-y-4">
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary inline-block text-sm underline"
          >
            Visit Live Site &rarr;
          </a>
        </section>
      )}

      {/* Metrics Section */}
      {project.metrics && <ProjectMetrics metrics={project.metrics} />}

      {/* Mid-page CTA */}
      <PortfolioCTA clientTypes={project.clientTypes} />

      {/* Client Testimonial */}
      {project.testimonial && <ProjectTestimonialComponent testimonial={project.testimonial} />}

      {/* Related Projects */}
      <RelatedProjects projects={relatedProjects} currentSlug={project.slug} />

      {/* Bottom CTA */}
      <PortfolioCTA clientTypes={project.clientTypes} />
    </div>
  )
}
